// Mock data for Work Execution modules
export interface WorkExecutionBatch {
  id: string;
  batchNumber: string;
  woId: string;
  woNumber: string;
  section: string;
  contractor: string;
  status: 'Draft' | 'Submitted' | 'Checked' | 'Verified' | 'Approved' | 'Rejected';
  stage: 'Submitted' | 'Checked' | 'Verified' | 'Final';
  executionPeriod: {
    from: string;
    to: string;
  };
  submittedOn: string;
  submittedBy: string;
  lastActionBy?: string;
  lastActionDate?: string;
  slaAge: number;
  totalLines: number;
  totalUpdateQty: number;
  notes?: string;
  attachments: Array<{
    id: string;
    fileName: string;
    fileType: string;
    fileUrl: string;
  }>;
  comments?: string;
}

export interface WorkExecutionLine {
  batchId: string;
  workLineId: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  section: string;
  uom: string;
  approvedQuantity: number;
  executedQuantity: number; // This update
  cumulativeExecuted: number; // Till date before this update
  percentCompletion: number; // Auto-calculated
  remainingQuantity: number; // Auto-calculated
  remarks?: string;
  reviewerAdjustment?: number; // For approvals
}

export interface ApprovedWorkLine {
  id: string;
  woId: string;
  woNumber: string;
  workLineId: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  section: string;
  site: string;
  project: string;
  contractor: string;
  uom: string;
  contractQuantity: number;
  cumulativeApprovedQty: number;
  percentCompletion: number;
  lastFinalApproval: string;
  retentionFlag: boolean;
  holdFlag: boolean;
  approvalHistory: Array<{
    batchId: string;
    stage: string;
    approvedQty: number;
    approvedOn: string;
    approvedBy: string;
    cumulativeAfter: number;
    remarks?: string;
  }>;
}

// Mock Work Execution Batches
export const mockExecutionBatches: WorkExecutionBatch[] = [
  {
    id: '1',
    batchNumber: 'EXB/2025/0091',
    woId: '3',
    woNumber: 'WO/2025-26/HYD/0044',
    section: 'Basement',
    contractor: 'Galaxy Infra',
    status: 'Submitted',
    stage: 'Submitted',
    executionPeriod: {
      from: '2025-08-10',
      to: '2025-08-15'
    },
    submittedOn: '2025-08-16T10:30:00',
    submittedBy: 'Kiran Kumar',
    slaAge: 1,
    totalLines: 12,
    totalUpdateQty: 140,
    notes: 'Foundation work completed for north bay area',
    attachments: [
      {
        id: 'att1',
        fileName: 'foundation_progress_photos.pdf',
        fileType: 'PDF',
        fileUrl: '/documents/foundation_progress_photos.pdf'
      }
    ]
  },
  {
    id: '2',
    batchNumber: 'EXB/2025/0092',
    woId: '5',
    woNumber: 'WO/2025-26/HYD/0046',
    section: 'Podium',
    contractor: 'PowerTech Electrical Services',
    status: 'Checked',
    stage: 'Checked',
    executionPeriod: {
      from: '2025-08-12',
      to: '2025-08-18'
    },
    submittedOn: '2025-08-19T14:20:00',
    submittedBy: 'Rajesh Patel',
    lastActionBy: 'QS Manager',
    lastActionDate: '2025-08-20T09:15:00',
    slaAge: 2,
    totalLines: 8,
    totalUpdateQty: 85,
    notes: 'Electrical conduit installation progress',
    attachments: [
      {
        id: 'att2',
        fileName: 'electrical_measurements.xlsx',
        fileType: 'XLSX',
        fileUrl: '/documents/electrical_measurements.xlsx'
      }
    ]
  },
  {
    id: '3',
    batchNumber: 'EXB/2025/0090',
    woId: '3',
    woNumber: 'WO/2025-26/HYD/0044',
    section: 'Superstructure',
    contractor: 'Galaxy Infra',
    status: 'Approved',
    stage: 'Final',
    executionPeriod: {
      from: '2025-08-05',
      to: '2025-08-12'
    },
    submittedOn: '2025-08-13T11:45:00',
    submittedBy: 'Kiran Kumar',
    lastActionBy: 'Project Manager',
    lastActionDate: '2025-08-15T16:30:00',
    slaAge: 0,
    totalLines: 6,
    totalUpdateQty: 220,
    notes: 'Concrete pouring completed',
    attachments: [
      {
        id: 'att3',
        fileName: 'concrete_test_reports.pdf',
        fileType: 'PDF',
        fileUrl: '/documents/concrete_test_reports.pdf'
      }
    ]
  }
];

// Mock Work Execution Lines
export const mockExecutionLines: WorkExecutionLine[] = [
  {
    batchId: '1',
    workLineId: 'WOL-005',
    workLineNumber: 'WOL-005',
    workLineTitle: 'Concrete Paving',
    description: 'Concrete paving for external areas',
    section: 'Basement',
    uom: 'Sqm',
    approvedQuantity: 2500,
    executedQuantity: 450,
    cumulativeExecuted: 1200,
    percentCompletion: 66.0,
    remainingQuantity: 850,
    remarks: 'North section completed'
  },
  {
    batchId: '1',
    workLineId: 'WOL-006',
    workLineNumber: 'WOL-006',
    workLineTitle: 'Interlocking Tiles',
    description: 'Interlocking tile laying',
    section: 'Basement',
    uom: 'Sqm',
    approvedQuantity: 800,
    executedQuantity: 150,
    cumulativeExecuted: 300,
    percentCompletion: 56.25,
    remainingQuantity: 350,
    remarks: 'Quality checks completed'
  },
  {
    batchId: '2',
    workLineId: 'WOL-007',
    workLineNumber: 'WOL-007',
    workLineTitle: 'Electrical Conduits',
    description: 'PVC conduit installation',
    section: 'Podium',
    uom: 'Mtr',
    approvedQuantity: 1500,
    executedQuantity: 280,
    cumulativeExecuted: 680,
    percentCompletion: 64.0,
    remainingQuantity: 540,
    remarks: 'Main distribution completed'
  }
];

// Mock Approved Work Lines
export const mockApprovedWorkLines: ApprovedWorkLine[] = [
  {
    id: '1',
    woId: '3',
    woNumber: 'WO/2025-26/HYD/0044',
    workLineId: 'WOL-005',
    workLineNumber: 'WOL-005',
    workLineTitle: 'Concrete Paving',
    description: 'Concrete paving for external areas including parking lots and walkways',
    section: 'External Works',
    site: 'HYD/EXT',
    project: 'Hyderabad Tower Complex',
    contractor: 'BuildRight Solutions Ltd',
    uom: 'Sqm',
    contractQuantity: 2500,
    cumulativeApprovedQty: 2500,
    percentCompletion: 100.0,
    lastFinalApproval: '2025-08-15',
    retentionFlag: false,
    holdFlag: false,
    approvalHistory: [
      {
        batchId: 'EXB/2025/0088',
        stage: 'Final',
        approvedQty: 800,
        approvedOn: '2025-08-08',
        approvedBy: 'PM - Rajesh',
        cumulativeAfter: 1200,
        remarks: 'Initial phase completed'
      },
      {
        batchId: 'EXB/2025/0090',
        stage: 'Final',
        approvedQty: 1300,
        approvedOn: '2025-08-15',
        approvedBy: 'PM - Rajesh',
        cumulativeAfter: 2500,
        remarks: 'Work completed'
      }
    ]
  },
  {
    id: '2',
    woId: '3',
    woNumber: 'WO/2025-26/HYD/0044',
    workLineId: 'WOL-006',
    workLineNumber: 'WOL-006',
    workLineTitle: 'Interlocking Tiles',
    description: 'Interlocking tile laying for pedestrian areas',
    section: 'External Works',
    site: 'HYD/EXT',
    project: 'Hyderabad Tower Complex',
    contractor: 'BuildRight Solutions Ltd',
    uom: 'Sqm',
    contractQuantity: 800,
    cumulativeApprovedQty: 480,
    percentCompletion: 60.0,
    lastFinalApproval: '2025-08-12',
    retentionFlag: true,
    holdFlag: false,
    approvalHistory: [
      {
        batchId: 'EXB/2025/0089',
        stage: 'Final',
        approvedQty: 320,
        approvedOn: '2025-08-10',
        approvedBy: 'QS - Manager',
        cumulativeAfter: 320,
        remarks: 'Phase 1 approved'
      },
      {
        batchId: 'EXB/2025/0091',
        stage: 'Final',
        approvedQty: 160,
        approvedOn: '2025-08-12',
        approvedBy: 'QS - Manager',
        cumulativeAfter: 480,
        remarks: 'Phase 2 approved'
      }
    ]
  },
  {
    id: '3',
    woId: '5',
    woNumber: 'WO/2025-26/HYD/0046',
    workLineId: 'WOL-007',
    workLineNumber: 'WOL-007',
    workLineTitle: 'Electrical Installation',
    description: 'Complete electrical installation including main distribution panel',
    section: 'MEP Works',
    site: 'HYD/TWR-B',
    project: 'Hyderabad Tower Complex',
    contractor: 'PowerTech Electrical Services',
    uom: 'LS',
    contractQuantity: 1,
    cumulativeApprovedQty: 0.75,
    percentCompletion: 75.0,
    lastFinalApproval: '2025-08-14',
    retentionFlag: true,
    holdFlag: false,
    approvalHistory: [
      {
        batchId: 'EXB/2025/0092',
        stage: 'Final',
        approvedQty: 0.25,
        approvedOn: '2025-08-08',
        approvedBy: 'Technical Head',
        cumulativeAfter: 0.25,
        remarks: 'Initial installation approved'
      },
      {
        batchId: 'EXB/2025/0094',
        stage: 'Final',
        approvedQty: 0.50,
        approvedOn: '2025-08-14',
        approvedBy: 'Technical Head',
        cumulativeAfter: 0.75,
        remarks: 'Main distribution completed'
      }
    ]
  }
];

// Work Order Sections with execution eligibility
export const mockWorkOrderSections = [
  {
    woId: '3',
    woNumber: 'WO/2025-26/HYD/0044',
    sections: [
      {
        name: 'External Works',
        linesCount: 14,
        progressPercentage: 58.5,
        lastUpdate: '2025-08-15',
        eligibleToUpdate: true,
        totalPendingQty: 820
      },
      {
        name: 'Landscaping',
        linesCount: 9,
        progressPercentage: 34.2,
        lastUpdate: '2025-08-12',
        eligibleToUpdate: true,
        totalPendingQty: 450
      }
    ]
  },
  {
    woId: '5',
    woNumber: 'WO/2025-26/HYD/0046',
    sections: [
      {
        name: 'MEP Works',
        linesCount: 18,
        progressPercentage: 72.8,
        lastUpdate: '2025-08-14',
        eligibleToUpdate: true,
        totalPendingQty: 0.65
      },
      {
        name: 'Fire Safety',
        linesCount: 12,
        progressPercentage: 45.0,
        lastUpdate: '2025-08-10',
        eligibleToUpdate: false,
        totalPendingQty: 0
      }
    ]
  }
];